import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Landmark, ChevronLeft, User } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { bsiInternetBankApi, type BsiIbProfileSummary } from '@/lib/banks/bsi/api';

const BanksListPage: React.FC = () => {
  const [profiles, setProfiles] = React.useState<BsiIbProfileSummary[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    bsiInternetBankApi
      .profiles()
      .then((res) => {
        if (!cancelled) setProfiles(res.data.items || []);
      })
      .catch(() => {
        if (!cancelled) setProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openProfile = async (id: number) => {
    try {
      await bsiInternetBankApi.activateProfile(id);
    } catch {
      // حتی اگر فعال‌سازی شکست خورد، به صفحه بانک برو
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="بانک‌ها"
        description="اتصال به اینترنت‌بانک‌ها. برای صادرات می‌توانید چند کاربر (حقوقی / حقیقی) همزمان داشته باشید."
        actions={
          <Button asChild variant="outline">
            <Link to="/bank-accounts">
              <Landmark className="ml-1 h-4 w-4" />
              حساب‌های بانکی داخلی
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center text-sm text-slate-500">در حال بارگذاری پروفایل‌ها…</CardContent>
          </Card>
        ) : profiles.length ? (
          profiles.map((p) => {
            const Icon = p.portal === 'personal' ? User : Building2;
            return (
              <Card key={p.id} className="border-slate-200 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Icon className="h-4 w-4 text-sky-700" />
                    {p.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600">
                    {p.portal_label}
                    <span className="mx-1 text-slate-300">·</span>
                    <span dir="ltr" className="font-mono text-xs">
                      {p.base_url}
                    </span>
                  </p>
                  <ul className="list-inside list-disc text-xs text-slate-500">
                    <li>{p.session_valid ? 'سشن فعال' : 'نیاز به ورود'}</li>
                    <li>سشن مستقل از سایر پروفایل‌ها</li>
                    {p.default_username ? <li>کاربر پیش‌فرض: {p.default_username}</li> : null}
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/banks/bsi" onClick={() => void openProfile(p.id)}>
                      ورود به پنل
                      <ChevronLeft className="mr-1 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="h-4 w-4 text-sky-700" />
                بانک صادرات ایران
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">حقوقی و حقیقی — پروفایل‌ها بعد از اولین ورود ساخته می‌شوند.</p>
              <Button asChild className="w-full">
                <Link to="/banks/bsi">
                  ورود به پنل بانک
                  <ChevronLeft className="mr-1 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-dashed border-slate-300 bg-slate-50/60">
          <CardHeader>
            <CardTitle className="text-base text-slate-500">بانک‌های بعدی</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-500">
            به‌تدریج بانک‌های دیگر هم به همین لیست اضافه می‌شوند.
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BanksListPage;
